import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { StudiesLayout } from "@/components/layouts";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useSegment } from "@/hooks/use-segment";
import { AssetUploadDropzone, useUploadsStore } from "@/components/ui/drag-drop-upload";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { useGetAssets } from "@/api/studio/assets/queries";

/* Prop Types */
interface StudySceneSelectRouteProps {
  projectId: string;
  studyId: string;
}

/*
 * StudySceneSelectRoute component
 * Here the user stages the assets they want to upload to their study
 */
export const StudySceneSelectRoute = ({ projectId, studyId }: StudySceneSelectRouteProps) => {
  /* Hooks */
  const { segmentPageEvent } = useSegment();
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-select");

  /* Stores */
  const { reset } = useUploadsStore((state) => state.uploadActions);

  /* Queries */
  const { data: assetsData } = useGetAssets(projectId, studyId);

  /* Derived state */
  const canProceed = !!assetsData?.assets?.length;

  /* Side Effects */
  useEffect(() => {
    // Reset the uploads store when the component mounts as we are starting a new upload process
    reset();
  }, [reset]);

  const handleUpload = () => {
    // Segment event for moving to the next step
    segmentPageEvent("Study Assets Submitted For AOI Review", {
      projectId,
      studyId,
      studyType: "scene",
    });
    navigateToNext();
  };

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-select">
      {/* Primary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">Upload scenes</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" disabled={!canProceed} onClick={handleUpload}>
            Next: Review scenes <ChevronRightIcon className="size-5" />
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Secondary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <div>
            <p className="text-base-content/70">This is where you upload images or digital renders of the your assets in context.</p>
            <p className="text-base-content/70">Supported file types: JPG, JPEG and PNG. High resolution uploads are scaled down for performance optimisation.</p>
          </div>
        </ToolbarItem>
      </Toolbar>

      {/* Drag & Drop */}
      <AssetUploadDropzone
        projectId={projectId}
        studyId={studyId}
        dragDropText="or select scenes from your computer"
        dragDropVideoUrl="/videos/upload-context-animation.webm"
        dragDropVideoClassName="w-64"
        segmentTrackName="Study"
        segmentTrackMediaType="Asset"
        additionalSegmentProperties={{ studyId, projectId, studyType: "scene" }}
        selectAssetsText="Select scenes"
        firstColumnHeader="Scene"
      />
    </StudiesLayout>
  );
};
